import { useEffect, useState, useCallback } from "react";
import { MagnifyingGlass, Check, X, Trash, House, Star, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { fileUrl, formatINR } from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700", approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700", inactive: "bg-stone-200 text-stone-600",
};

export default function AdminProperties() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);
  const [offerFor, setOfferFor] = useState(null);
  const [offer, setOffer] = useState({ active: false, offer_price: "", valid_until: "" });

  const openOffer = (p) => {
    setOfferFor(p);
    setOffer({ active: !!p.offer_active, offer_price: p.offer_price || "", valid_until: p.offer_valid_until || "" });
  };
  const saveOffer = async () => {
    try {
      await api.patch(`/admin/properties/${offerFor.id}/offer`, {
        active: !!offer.active, offer_price: Number(offer.offer_price) || 0, valid_until: offer.valid_until || "",
        label: "Special Offer", promotion_type: "Special Offer",
      });
      toast.success("Offer updated");
      setOfferFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save offer"); }
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (category) p.set("category", category);
    setItems(null);
    api.get(`/admin/properties?${p.toString()}`).then((r) => setItems(r.data));
  }, [q, status, category]);

  useEffect(() => { load(); }, [status, category, load]);

  const setStatusFor = async (id, st) => {
    await api.patch(`/admin/properties/${id}/status`, { status: st });
    toast.success(`Property ${st}`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    load();
  };
  const toggleFeatured = async (p) => {
    await api.patch(`/admin/properties/${p.id}/featured`, { featured: !p.featured });
    toast.success(!p.featured ? "Added to Staff Favourites" : "Removed from Staff Favourites");
    load();
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Properties</h1>
      <div className="mt-4 flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-1">
          <MagnifyingGlass size={18} className="text-stone-400" />
          <Input data-testid="ap-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="border-0 shadow-none focus-visible:ring-0" />
        </form>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ap-status" className="w-40 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ap-category" className="w-44 rounded-full"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-3">
        {items === null ? (
          [1, 2, 3].map((i) => <SkeletonRow key={i} />)
        ) : items.length === 0 ? (
          <EmptyState icon={House} title="No properties" message="No properties match your filters." />
        ) : (
          items.map((p) => (
            <div key={p.id} data-testid={`admin-property-${p.id}`} className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 lg:flex-row lg:items-center">
              <img src={fileUrl(p.images?.[0])} alt={p.name} className="h-20 w-full rounded-xl object-cover lg:w-28" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-semibold text-stone-900">{p.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                </div>
                <p className="text-sm text-stone-500">{p.category} · {p.location} · Owner: {p.owner_name}</p>
                <p className="mt-1 font-heading font-bold text-stone-900">{formatINR(p.prices?.starting)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.status !== "approved" && <Button data-testid={`approve-${p.id}`} size="sm" onClick={() => setStatusFor(p.id, "approved")} className="gap-1 rounded-xl bg-primary hover:bg-green-900"><Check size={14} /> Approve</Button>}
                {p.status !== "rejected" && <Button data-testid={`reject-${p.id}`} size="sm" variant="outline" onClick={() => setStatusFor(p.id, "rejected")} className="gap-1 rounded-xl text-destructive"><X size={14} /> Reject</Button>}
                {p.status === "approved" && <Button size="sm" variant="outline" onClick={() => setStatusFor(p.id, "inactive")} className="rounded-xl">Deactivate</Button>}
                <Button data-testid={`feature-${p.id}`} size="sm" variant="outline" onClick={() => toggleFeatured(p)} className={`rounded-xl ${p.featured ? "text-amber-500" : ""}`} title="Staff Favourite"><Star size={14} weight={p.featured ? "fill" : "regular"} /></Button>
                {p.status === "approved" && <Button data-testid={`offer-btn-${p.id}`} size="sm" variant="outline" onClick={() => openOffer(p)} className={`rounded-xl ${p.offer_active ? "text-red-600" : ""}`} title="Special Offer"><Tag size={14} weight={p.offer_active ? "fill" : "regular"} /></Button>}
                <Button data-testid={`admin-del-${p.id}`} size="sm" variant="outline" onClick={() => del(p.id)} className="rounded-xl text-destructive"><Trash size={14} /></Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!offerFor} onOpenChange={(o) => !o && setOfferFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Special Offer — {offerFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-500">Starting price: <span className="font-semibold">{formatINR(offerFor?.prices?.starting)}</span></p>
            <div className="flex items-center justify-between"><span className="text-sm text-stone-600">Offer active</span><Switch data-testid="offer-active" checked={offer.active} onCheckedChange={(v) => setOffer({ ...offer, active: v })} /></div>
            <div><Label className="text-xs text-stone-500">Offer price ₹ (must be less than starting)</Label><Input data-testid="offer-price" type="number" value={offer.offer_price} onChange={(e) => setOffer({ ...offer, offer_price: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs text-stone-500">Valid until</Label><Input data-testid="offer-valid" type="date" value={offer.valid_until} onChange={(e) => setOffer({ ...offer, valid_until: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter><Button data-testid="offer-save" onClick={saveOffer} className="rounded-xl bg-primary hover:bg-green-900">Save Offer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
